/**
 * Localiza, dentro de lo guardado en IndexedDB, el archivo al que se refiere una notificación de
 * análisis.
 *
 * Hay dos formas de identificarlo y conviven a propósito. El emisor histórico manda
 * `photo_id + uuid`; la Lambda identifica por **key de S3** y el contrato de LETPWA-419 declara `key`
 * obligatorio y los otros dos opcionales (verificado en el esquema implementado del gateway,
 * `src/Core/Validation/PhotoEvents.ts`, no en el documentado). Un evento que solo trae `key` hoy se
 * descarta en silencio, que es el defecto que 421 viene a cerrar.
 *
 * El cruce por key funciona porque `publicUrl = URL_AWS + Key` en el Storage Manager
 * (`Utils/Storage/index.ts:62`): la URL guardada **termina** en el key. Por eso alcanza con
 * `includes`, y por eso sirve igual si el emisor manda el key completo (`let/i50/let-pwa_163_x.jpg`)
 * o solo el nombre del archivo — la Lambda llama `key_path` al nombre, y las dos formas son sufijo
 * de la misma URL.
 *
 * **Ante ambigüedad no se elige: se rechaza.** Marcar la foto equivocada es peor que no marcar
 * ninguna, porque el asegurado corrige una que estaba bien y la mala sigue ahí.
 */

/** Lo mínimo que el localizador necesita de una entrada de IndexedDB. */
export interface StoredPhotoEntry {
  photoId: number;
  files: { uuid?: string; data?: unknown }[];
}

export interface AnalysisTarget {
  inspection_id?: number;
  key?: string;
  photo_id?: number;
  uuid?: string;
}

export type MatchResult =
  | { found: true; photoId: number; fileIdx: number; via: 'ids' | 'key' }
  | { found: false; reason: 'no_match' | 'ambiguous_key' | 'insufficient_input' };

/**
 * Un key más corto que esto no discrimina y podría casar con cualquier URL. El contrato lo declara
 * obligatorio, así que en la práctica no ocurre; la guarda existe para que un valor degenerado
 * falle en vez de marcar la primera foto que encuentre.
 */
const MIN_KEY_LENGTH = 8;

/**
 * El cruce solo vale contra una `publicUrl`, no contra cualquier string.
 *
 * No alcanza con comprobar que `data` sea texto: `addOptionalPhoto` guarda ahí el **contenido** de la
 * foto en base64, y un `includes` sobre eso puede casar por coincidencia de bytes. Verificado — el
 * arnés lo atrapó con una foto opcional. Una `publicUrl` siempre es `URL_AWS + Key`, o sea que
 * empieza por http.
 */
function isPublicUrl(value: unknown): boolean {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

function locateByIds(
  entries: StoredPhotoEntry[],
  photoId: number,
  uuid: string,
): MatchResult | null {
  const entry = entries.find((candidate) => candidate.photoId === photoId);
  if (!entry) {
    return null;
  }
  const fileIdx = entry.files.findIndex((file) => file.uuid === uuid);
  return fileIdx === -1 ? null : { found: true, photoId, fileIdx, via: 'ids' };
}

function locateByKey(entries: StoredPhotoEntry[], key: string): MatchResult | null {
  if (key.length < MIN_KEY_LENGTH) {
    return null;
  }
  const hits: { photoId: number; fileIdx: number }[] = [];
  for (const entry of entries) {
    entry.files.forEach((file, fileIdx) => {
      if (isPublicUrl(file.data) && (file.data as string).includes(key)) {
        hits.push({ photoId: entry.photoId, fileIdx });
      }
    });
  }
  if (hits.length === 1) {
    return { found: true, ...hits[0], via: 'key' };
  }
  return hits.length > 1 ? { found: false, reason: 'ambiguous_key' } : null;
}

/**
 * Prefiere `photo_id + uuid` cuando vienen los dos, y cae al key si no vienen o si no encontraron
 * nada. El orden lo fija el criterio de la historia: «si el evento trae photo_id + uuid se usa el
 * matching actual; si solo trae key, se busca la foto cuya publicUrl contiene ese key».
 *
 * El fallback cuando los ids **vinieron pero no casaron** no está en el criterio y es deliberado:
 * un id que no resuelve y un key que sí es el caso de una foto resubida, y descartarla sería
 * reproducir el descarte silencioso que la historia viene a eliminar.
 */
export function locateAnalyzedFile(
  entries: StoredPhotoEntry[],
  target: AnalysisTarget,
): MatchResult {
  const { photo_id, uuid, key } = target;

  if (photo_id !== undefined && photo_id !== null && uuid) {
    const byIds = locateByIds(entries, photo_id, uuid);
    if (byIds) {
      return byIds;
    }
  }

  if (key) {
    const byKey = locateByKey(entries, key);
    if (byKey) {
      return byKey;
    }
  }

  const hadSomethingToLookFor = Boolean(key) || (photo_id !== undefined && Boolean(uuid));
  return { found: false, reason: hadSomethingToLookFor ? 'no_match' : 'insufficient_input' };
}
